// X214 (Grant 2026-10-28, the match scheduling dialog: "Why not use the date picker we already use for scheduling,
// then offer court/playing area options. I always prefer the table view for court bookings ... It's not even clear that
// I am booking a court at all."): the pure half of the schedule-and-book picker (components/bookings/TimeAndPlacePicker.vue),
// unit tested in tests/unit/schedule-booking-x214.test.ts. Three steps: 1 When (the date picker and the 24h time selects
// every scheduling form uses), 2 Book a court (the member /book table: playing areas as columns, the day's slots as rows,
// drawn by the shared BookingDayGrid and BookingCell), 3 the summary line and the one button that says what happens.
// The data is GET bookings/scheduling (SchedulingAvailabilityResponse): a cell's `status` says whether a match can start
// there (free for the whole match), its `slotStatus` (X214, additive) what that one slot is on its own.
import {CELL_STATES, columnBlocks} from '~/utils/bookingWeek';
import {AREA_NOUN, aOrAn, areaWords, cap as capWord} from '~/utils/areaWords';
import {clock24, clockInZone, gridTime, instantInZone} from '~/utils/bookings';
import {formatMinor} from '~/utils/adminActionCopy';
import {chosenCell, dateOfInstant, dateWord, dayOpenPlay, findDay, findTime, lengthWord, PICKER_MODES} from '~/utils/schedulingPicker';

const ms = (iso) => (iso ? new Date(iso).getTime() : NaN);
const sameInstant = (a, b) => !Number.isNaN(ms(a)) && ms(a) === ms(b);

/**
 * F-FIX6 (TEST_FT-COMBO H1): the minutes the booking covers, as the API sized the table it sent. An americano match books
 * its division's whole session (rounds × the match length, BE10), so its table, summary and confirm use the answer's
 * bookingMinutes (the request's minutes= stays one match's length; the API multiplies). Any other match: the admin's
 * one-off length, else the match length, as before.
 */
export function bookingMinutesOf(availability, choice = null) {
  if (Number(availability?.sessionRounds) > 1 && Number(availability?.bookingMinutes) > 0) return Number(availability.bookingMinutes);
  return Number(choice?.durationMinutes) || Number(availability?.matchMinutes) || 60;
}

/** F-FIX6: "match" or "session" (an americano's rounds back to back), for the words that name what runs on. */
export function bookingNoun(availability) {
  return Number(availability?.sessionRounds) > 1 ? 'session' : 'match';
}

/** The dialog's title: "Schedule Sam Jones v Alex Smith". */
export function scheduleTitle(homeName, awayName) {
  if (homeName && awayName) return `Schedule ${homeName} v ${awayName}`;
  return 'Schedule the match';
}

/**
 * The club's word for its playing areas in this picker, from the areas' own names ("Court 1", "Court 2" → "court";
 * "Table 5" → "table"). Grant 2026-11-02: a mixed set names every noun, "court or table" (areaWords); "playing area"
 * only when no name says anything.
 */
export function areaNounOf(availability) {
  return areaWords(availability).noun;
}

/** Step 2's heading: "Book a court" · "Book a court or table" · "Book an oche" · "Book a playing area". */
export function bookStepTitle(noun) {
  return `Book ${aOrAn(noun || AREA_NOUN)}`;
}

/** X243 (TEST_FT-R3 O-1): what a period past the club's midnight says after its end, as the hours painter does. */
export const NEXT_DAY = ' next day';

/**
 * X243: "20:00–21:30", or "20:00–0:30 next day" when it runs past the club's midnight, on the club's grid clock: the
 * summary, the table's cells and their tooltips. X248 (TEST_FT-R6 obs. 3): a match ending on the club's midnight itself
 * reads "22:30–0:00 next day", as one ending after it does (was "22:30–24:00"). A slot row's own period keeps the grid's
 * "23:30–24:00" for the day's end (the booking grid's time column, X91). The app's gridRange says the same.
 */
export function gridRange(startIso, endIso, zone) {
  return `${gridTime(startIso, zone)}–${rangeEnd(startIso, endIso, zone)}`;
}

/** X248: a match range's end: "21:30", "0:30 next day", and "0:00 next day" on the club's midnight itself. */
export function rangeEnd(startIso, endIso, zone) {
  const next = nextDayWord(startIso, endIso, zone);
  if (next) return `${gridTime(endIso, zone)}${next}`;
  if (startIso && endIso && ms(endIso) > ms(startIso) && clockInZone(endIso, zone) === '00:00') return `${gridTime(endIso, zone)}${NEXT_DAY}`;
  return gridTime(endIso, zone, {end: true});
}

/** X243: NEXT_DAY when `endIso` (less a minute) falls on a later club day than `startIso`, else ''. */
export function nextDayWord(startIso, endIso, zone) {
  if (!startIso || !endIso) return '';
  const last = ms(endIso) - 60000;
  if (!(last > ms(startIso))) return '';
  const a = dateOfInstant(new Date(ms(startIso)).toISOString(), zone);
  const b = dateOfInstant(new Date(last).toISOString(), zone);
  return a && b && b > a ? NEXT_DAY : '';
}

/** How many slots a match of `minutes` books on an area with `slotMinutes` slots: rounded up, never under one (B7c). */
export function slotsForMatch(minutes, slotMinutes) {
  const m = Math.max(1, parseInt(minutes, 10) || 60);
  const s = Math.max(1, parseInt(slotMinutes, 10) || 60);
  return Math.max(1, Math.ceil(m / s));
}

const minutesInZone = (iso, zone) => {
  const [h, m] = clockInZone(iso, zone).split(':').map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
};

/** "HH:mm" (two-digit, the time select's value) of an instant on the club's clock. */
export function hhmmInZone(iso, zone) {
  return iso ? clockInZone(iso, zone) : null;
}

/** The day's offered start whose clock reads `hhmm` ("19:00"), or null. */
export function timeAtClock(day, hhmm, zone) {
  if (!day || !hhmm) return null;
  return (day.times ?? []).find((t) => hhmmInZone(t.startsAt, zone) === hhmm) ?? null;
}

/** The yyyy-MM-dd `n` days from `date`. */
export function shiftDate(date, n) {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whether the loaded window holds `date` (the picker reloads from the date otherwise). */
export function dateLoaded(availability, date) {
  return !!findDay(availability, date);
}

/** X231 (F-FIX3 decision 1, the app's CellLook.tooShort): the scheduling table's own state on top of the member grid's
 *  CELL_STATES: a free start the match would run past (into a booking, a freeze, closing time). Amber tint (X232) with
 *  "Too short" (BookingCell .bwc--short), never the white of a free cell. */
export const SCHEDULE_STATES = Object.freeze({short: {cls: 'short'}});

// X234 (Grant 2026-10-29): a club block (the API's label "Club") is "The club", as /book says it, never "booked".
const TAKEN_WORDS = {Match: 'a match is on', Meet: 'a meet is on', Club: 'The club'};

/**
 * One cell of the table: what the slot is on its own (the colour, the BookingCell state) and whether a match can start
 * there (`bookable`: free for the whole match, or this match's own booking). A free slot the match would run past
 * (into a booking, a freeze, closing time) is "Too short" (X231: grey, as the app), can't be picked, and says why.
 */
export function scheduleCell(time, area, ctx = {}) {
  const c = (time?.areas ?? []).find((x) => x.areaId === area?.id) ?? null;
  const zone = ctx.zone ?? null;
  const slotEnd = new Date(ms(time?.startsAt) + (Number(area?.slotMinutes) || Number(ctx.slotMinutes) || 60) * 60000).toISOString();
  // One instant form (toISOString) on every cell, so a block's end meets the next cell's start (columnBlocks joins).
  const base = {
    startsAt: time?.startsAt ? new Date(time.startsAt).toISOString() : null, endsAt: slotEnd, matchEndsAt: c?.endsAt ?? null, date: ctx.date ?? null, areaId: area?.id ?? null,
    bookable: false, past: false, text: '', mini: '', bookingKey: null, realBooking: false, priceMinor: c?.priceMinor ?? null, forMatches: !!c?.forMatches,
  };
  const done = (state, extra) => { const st = CELL_STATES[state] ?? SCHEDULE_STATES[state]; return {...base, state, cls: st.cls, icon: st.icon ?? null, ...extra}; };
  const name = area?.name ?? capWord(`this ${ctx.noun ?? AREA_NOUN}`);
  const from = gridTime(time?.startsAt, zone);
  const slotRange = `${from}–${gridTime(slotEnd, zone, {end: true})}${nextDayWord(time?.startsAt, slotEnd, zone)}`;
  // X238 (TEST_R99 L-7): a cell names the match's own times (its start plus the match, or the americano session), as
  // the summary does, never the booked span (shorter before open play, longer when slots round it up).
  const matchMinutes = Number(ctx.matchMinutes) || 0;
  const matchEnd = matchMinutes > 0 && time?.startsAt ? new Date(ms(time.startsAt) + matchMinutes * 60000).toISOString() : c?.endsAt;
  const matchRange = matchEnd ? `${from}–${rangeEnd(time?.startsAt, matchEnd, zone)}` : slotRange;
  // X233 (R97 L-2): the picked span's overlay names the match's own times from its first cell.
  base.matchRange = matchRange;
  if (!c) return done('open', {text: 'Open play', tip: 'Open play: nothing to book then.'});
  const slot = c.slotStatus ?? c.status;
  // X219 (R89 M-1): "This match" is drawn on the slots the match's own booking occupies (slotStatus mine), never on a
  // start before it whose match would run into it. Old responses without slotStatus fall back to the whole-match status.
  const own = c.slotStatus ? c.slotStatus === 'mine' : c.status === 'mine';

  if (own) {
    const word = /held/i.test(c.slotLabel ?? c.label ?? '') ? 'Held' : 'This match';
    // X244 (TEST_R102 L-1): a row of the match's own block from where the whole session would run past closing is an
    // admin's to book anyway (the API marks it pastClosing + sessionFree), as any past-closing start: it asks first.
    const closing = c.status === 'closed' && !!c.pastClosing;
    return done('mine', {
      text: word, mini: word, bookable: c.status === 'mine' || c.status === 'free' || closing, bookingKey: 'mine', tipLead: `This match: ${name}`, tip: `This match: ${name}, ${matchRange}.`,
      ...(closing ? {pastClosing: true, closesAtLabel: c.closesAtLabel ?? null, priceMinor: c.pastClosingPriceMinor ?? null} : {}),
    });
  }
  if ((c.status === 'free' || c.status === 'mine') && slot === 'free') {
    const price = c.priceMinor > 0 ? formatMinor(c.priceMinor, ctx.currency || 'gbp') : null;
    const kept = c.forMatches ? ' Kept for matches at this time.' : '';
    return done('free', {
      text: price ?? 'Free', mini: price ?? 'Free', bookable: true, icon: c.forMatches ? 'mdi-whistle' : null,
      tip: `Available: ${name}, ${matchRange}, ${price ?? 'free'}.${kept}`,
    });
  }
  if (slot === 'taken') {
    const label = c.slotLabel || (c.slotStatus ? 'Booked' : c.label) || 'Booked';
    const word = label === 'Booked' ? 'Taken' : label;
    return done('taken', {text: word, mini: word.length <= 5 ? word : '', bookingKey: `taken|${label}`, tipLead: `Taken: ${TAKEN_WORDS[label] ?? 'booked'}`, tip: `Taken: ${TAKEN_WORDS[label] ?? 'booked'}, ${slotRange}.`});
  }
  if (slot === 'frozen') return done('frozen', {text: 'Frozen', tip: `Frozen${c.slotLabel && c.slotLabel !== 'Closed' ? ': ' + c.slotLabel : ''}. The club has closed ${name} for a while.`});
  if (slot === 'closed') return done('closed', {text: 'Closed', tip: `Closed: ${name} isn't open then.`});
  if (slot === 'open') return done('open', {text: 'Open play', tip: 'Open play: nothing to book then, first come first served.'});
  // The slot is free, the match isn't: it would run into something later.
  const why = {taken: 'it is booked', frozen: 'it is frozen', closed: 'it closes'}[c.status] ?? 'it is not free';
  const tip = `Too short for the ${ctx.what || 'match'}: ${name} is free at ${from}, but ${why} before ${ctx.lengthWord || 'the ' + (ctx.what || 'match')} is up.`;
  // X237 (Grant 2026-10-29, "Admins can, with a warning"): the API marks a start an admin may book anyway (it only runs
  // past closing time or into closed time, nothing booked or frozen in the way). It still reads Too short, but it can
  // be picked; the picker asks "This runs past closing" first (pastClosingConfirm).
  if (c.status === 'closed' && c.pastClosing) {
    return done('short', {
      text: 'Too short', mini: 'Too short', bookable: true, pastClosing: true, closesAtLabel: c.closesAtLabel ?? null, priceMinor: c.pastClosingPriceMinor ?? null,
      tip: `${tip} As an admin you can still book it.`,
    });
  }
  // X231 (F-FIX3 decision 1, as the app): its own grey look and word, still not pickable, the tooltip says why.
  return done('short', {text: 'Too short', mini: 'Too short', tip});
}

/**
 * X237 (Grant 2026-10-29): the confirm before an admin books a match that runs past closing, the same words as the app
 * (schedule_booking.dart pastClosingConfirm): "Court 1 closes at 22:00, before this 1 hour 30 minutes match is over.
 * Book it anyway?"
 */
/**
 * F-FIX6 (TEST_R99 L-2): a length said before a noun: "45-minute", "1-hour", "1½-hour", "2-hour", "100-minute".
 */
export function lengthAdjective(minutes) {
  const m = parseInt(minutes, 10);
  if (!m || m <= 0) return '';
  if (m % 60 === 0) return `${m / 60}-hour`;
  if (m > 60 && m % 30 === 0) return `${Math.floor(m / 60)}½-hour`;
  return `${m}-minute`;
}

export function pastClosingConfirm(areaName, closesAtLabel, minutes, what = 'match', noun = AREA_NOUN) {
  const length = lengthAdjective(minutes);
  const area = areaName || capWord(`the ${noun}`);
  const at = closesAtLabel ? ` at ${closesAtLabel}` : '';
  return {
    title: 'This runs past closing',
    body: `${area} closes${at}, before this ${length ? length + ' ' : ''}${what || 'match'} is over. Book it anyway?`,
    yes: 'Book anyway',
    no: 'Pick another time',
  };
}

/** X237: the summary line once an admin chose Book anyway (the app's schedule_booking.dart pastClosingLine). */
export function pastClosingLine(closesAtLabel) {
  return `Runs past closing${closesAtLabel ? ' at ' + closesAtLabel : ''}: booked as an admin override.`;
}

/**
 * X237: whether the chosen area books nothing then ('open' / 'closed'), or null. A closed start an admin chose to book
 * anyway (cell.pastClosing and choice.allowPastClosing) books, so it is not idle.
 */
export function cellIdle(cell, choice) {
  if (!cell) return null;
  if (cell.status === 'closed' && cell.pastClosing && choice?.allowPastClosing) return null;
  return ['open', 'closed'].includes(cell.status) ? cell.status : null;
}

/**
 * One day of the picker as the shared BookingDayGrid draws it: a row per offered start (the club's 24h clock, a time
 * past midnight keeps counting), a column per playing area in the club's own order, blocks by columnBlocks.
 *   {rows, step, columns: [{area, cells, blocks}], empty, closedAllDay, startedRows, openPlay}
 */
export function scheduleGrid(availability, date) {
  const zone = availability?.timeZone ?? null;
  const step = Number(availability?.slotMinutes) || 60;
  const day = findDay(availability, date);
  const blank = {rows: [], step, columns: [], empty: true, closedAllDay: false, startedRows: 0, openPlay: dayOpenPlay(day), day};
  if (!day || dayOpenPlay(day) || !(day.times ?? []).length) return blank;
  const times = [...day.times].sort((a, b) => ms(a.startsAt) - ms(b.startsAt));
  let wrap = 0;
  let last = -1;
  const rows = times.map((t) => {
    let m = minutesInZone(t.startsAt, zone) + wrap;
    if (m < last) { wrap += 1440; m += 1440; }
    last = m;
    return {minutes: m, label: clock24(m), hour: false, startsAt: t.startsAt};
  });
  rows.forEach((r, i) => { r.end = Math.min(r.minutes + step, rows[i + 1]?.minutes ?? Infinity); });
  let lastHour = null;
  for (const r of rows) {
    const h = Math.floor(r.minutes / 60);
    if (h !== lastHour) { r.hour = true; lastHour = h; }
  }
  const areas = [...(availability?.areas ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || String(a.name).localeCompare(String(b.name)));
  const matchMinutes = bookingMinutesOf(availability);
  // Grant 2026-11-02: the cells say the club's own noun for an unnamed area (scheduleCell's "This court").
  const ctx = {zone, date, currency: availability?.currency, slotMinutes: step, matchMinutes, lengthWord: lengthWord(matchMinutes), what: bookingNoun(availability), noun: areaNounOf(availability)};
  const columns = areas.map((area) => {
    const cells = times.map((t) => scheduleCell(t, area, ctx));
    const blocks = columnBlocks(cells, rows, step);
    // X244 (TEST_R102 L-4): a block of the match's own booking that started before the table (a session under way today:
    // the API offers no start that has gone) names the booking's whole span, and its first shown row keeps the match's
    // own start (`keepStartsAt`, X243's first-row rule), never the first start still to come.
    for (const b of blocks) {
      if (b.state !== 'mine') continue;
      const booking = (availability?.ownBookings ?? []).find((x) => x.areaId === area.id && ms(x.startsAt) < ms(b.startsAt) && ms(x.endsAt) > ms(b.startsAt));
      if (!booking) continue;
      const range = gridRange(booking.startsAt, booking.endsAt, zone);
      Object.assign(b, {range, sub: range, line: `${b.text} · ${range}`, keepStartsAt: new Date(booking.startsAt).toISOString(), ownRange: true});
      if (b.cell.tipLead) b.tip = `${b.cell.tipLead}, ${range}.`;
      if (b.cell) b.cell = {...b.cell, keepStartsAt: b.keepStartsAt};
    }
    // X240 (TEST_R100 L-5): the match's own booking block names the booking's whole span. Past closing it holds the court
    // to the match's end (F-FIX6), beyond the day's last row, so the rows alone would stop it at closing ("20:30–21:00").
    for (const b of blocks) {
      if (b.ownRange) continue;
      // R105 L-1: every own block reads its range as the summary does, so one ending on the club's midnight says
      // "22:30–0:00 next day", never the rows' "22:30–24:00".
      const matchEnd = b.state === 'mine' ? b.cell?.matchEndsAt : null;
      const end = b.state === 'mine' ? (matchEnd && ms(matchEnd) > ms(b.endsAt) ? matchEnd : b.endsAt) : null;
      if (!end) continue;
      const range = `${rows[b.row]?.label ?? gridTime(b.startsAt, zone)}–${rangeEnd(b.startsAt, end, zone)}`;
      Object.assign(b, {range, sub: range, line: `${b.text} · ${range}`});
      if (b.cell.tipLead) b.tip = `${b.cell.tipLead}, ${range}.`;
    }
    return {area, cells, blocks};
  });
  return {rows, step, columns, empty: !rows.length || !columns.length, closedAllDay: false, startedRows: 0, openPlay: false, day};
}

/**
 * The rows a match picked at `startsAt` on `areaId` covers, for BookingDayGrid's `selected`: from its start to the end
 * the API booked for it (the match length rounded up to the area's slots), at least the one row.
 */
export function selectedSpan(grid, areaId, startsAt) {
  const col = (grid?.columns ?? []).find((c) => c.area.id === areaId);
  if (!col) return null;
  const i = col.cells.findIndex((c) => sameInstant(c.startsAt, startsAt));
  if (i < 0) return null;
  const end = ms(col.cells[i].matchEndsAt);
  let n = 0;
  for (let r = i; r < col.cells.length; r++) {
    if (Number.isNaN(end) ? r === i : ms(col.cells[r].startsAt) < end) n++;
    else break;
  }
  return {areaId, startsAt: col.cells[i].startsAt, slots: Math.max(1, n)};
}

/**
 * X233 (R97 L-2): the picked match drawn as ONE block over the rows it covers (BookingDayGrid noDrag), so a row inside
 * it that is Too short (or Taken by this match's own booking…) on its own never shows its word in white on red.
 * `selected` is selectedSpan's {areaId, startsAt, slots}; `hidden` the folded rows above. Null when nothing is picked
 * in this column or the pick is folded away.
 *   {row, span, text, sub}: text the match's times ("11:00–11:45"), sub the price word of its start ("Free", "£4.00").
 */
export function pickOverlay(col, selected, hidden = 0) {
  if (!col || !selected || selected.areaId !== col.area?.id) return null;
  const from = (col.cells ?? []).findIndex((c) => sameInstant(c.startsAt, selected.startsAt));
  if (from < 0) return null;
  const to = from + Math.max(1, selected.slots ?? 1);
  if (to <= hidden) return null;
  const row = Math.max(from, hidden) - hidden;
  const first = col.cells[from];
  // F-FIX6 (UX a): an admin's Book anyway pick is a pick like any other (never "Too short"), with the whole match's
  // times; its second line says when the club closes (TEST_R99: the court is held for the whole match).
  const closing = first.state !== 'free' && !!first.pastClosing && !!first.closesAtLabel;
  const sub = first.state === 'free' ? (first.text || null) : closing ? `Closes ${first.closesAtLabel}` : null;
  // X241 (TEST_FT-R2 L1): `closing` lets a short (one-row) block still say "Closes 22:00", where the price word waits
  // for a taller block; without it 21:00–22:30 reads as an ordinary pick.
  // X243: a one-row pick has room for the times alone; its "next day" is in the summary and the cell's tooltip.
  const span = to - hidden - row;
  const text = span > 1 ? (first.matchRange || '') : (first.matchRange || '').replace(NEXT_DAY, '');
  return {row, span, text, sub, ...(closing ? {closing: true} : {})};
}

/**
 * X219 (R89 M-2): where a NEW schedule's default time lands (tomorrow at the next half hour is rarely a slot start):
 * the first start at or after it, on that day or a later loaded one, where a match fits on some playing area for the
 * whole match. Null when the day is open play (nothing to book, keep the time) or no start is free in the window.
 *   {date, startsAt}
 */
export function firstFreeStart(availability, date, startsAt) {
  if (!date) return null;
  const target = ms(startsAt);
  const days = [...(availability?.dayList ?? [])].filter((d) => d?.date && d.date >= date).sort((a, b) => (a.date < b.date ? -1 : 1));
  for (const d of days) {
    if (dayOpenPlay(d)) {
      if (d.date === date) return null;
      continue;
    }
    const times = [...(d.times ?? [])].sort((a, b) => ms(a.startsAt) - ms(b.startsAt));
    const t = times.find((x) => (d.date > date || Number.isNaN(target) || ms(x.startsAt) >= target) && (x.areas ?? []).some((a) => a.status === 'free'));
    if (t) return {date: d.date, startsAt: t.startsAt};
  }
  return null;
}

/** X219 (R89 M-2): why a new schedule opens on No court needed: nothing is free for the whole match from its time on. */
export function noFreeStartLine(dateLabel, hhmm, noun = 'playing area') {
  const at = hhmm ? clock24(parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3, 5), 10)) : null;
  return `No ${noun} is free for the whole match${at ? ` from ${at}` : ''}${dateLabel ? ` on ${dateLabel}` : ''} or in the days after it. Pick another day, or keep the time with no ${noun}.`;
}

/**
 * X219 (R89 M-3): the day a scheduling load starts from for a time already chosen (a suggestion being answered): its
 * own date when it is after today, else null (today's 14 days hold it).
 */
export function loadFromFor(iso, zone, today) {
  if (!iso) return null;
  let local = zone;
  if (!local) { try { local = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { local = null; } }
  return firstLoadFrom(dateOfInstant(iso, local), today);
}

/**
 * The legend entries for the states on screen (the member grid's words, said for a match). Grant 2026-11-02: said with
 * the club's own noun ("the court already booked", "closed this court or board"), "playing area" only when none is known.
 */
export function scheduleLegend(noun = AREA_NOUN) {
  return Object.freeze([
    {key: 'free', cls: 'free', word: 'Available', sentence: 'Available: a match can start here. It shows the price of the booking, or Free.'},
    {key: 'forMatches', cls: 'free', icon: 'mdi-whistle', word: 'For matches', sentence: 'For matches: the club keeps this time for matches, and this match can have it.'},
    {key: 'taken', cls: 'taken', word: 'Taken', sentence: 'Taken: someone has booked it, or another match or a meet is on.'},
    {key: 'mine', cls: 'mine', word: 'This match', sentence: `This match: the ${noun} already booked or held for this match.`},
    {key: 'open', cls: 'open', icon: 'mdi-walk', word: 'Open play', sentence: 'Open play: nothing to book, first come first served.'},
    {key: 'frozen', cls: 'cant', icon: 'mdi-snowflake', word: 'Frozen', sentence: `Frozen: the club has closed this ${noun} for a while.`},
    {key: 'closed', cls: 'cant', icon: 'mdi-cancel', word: 'Closed', sentence: `Closed: the ${noun} isn't open then.`},
    // X231 (F-FIX3 decision 1): the app's words (schedule_booking.dart scheduleLegend 'short').
    {key: 'short', cls: 'short', word: 'Too short', sentence: 'Too short: free to start, but it runs into a booking or closing time before the match is over.'},
  ]);
}

/** The legend with no noun known (the fallback words). */
export const SCHEDULE_LEGEND = scheduleLegend();

/** The legend keys for the cells on screen, in the legend's order, said with the club's noun. */
export function scheduleLegendKeys(grid, noun = AREA_NOUN) {
  const keys = new Set();
  for (const col of grid?.columns ?? []) {
    for (const c of col.cells ?? []) {
      if (c.state === 'free' && c.forMatches && c.bookable) keys.add('forMatches');
      else keys.add(c.state);
    }
  }
  return scheduleLegend(noun).filter((l) => keys.has(l.key));
}

/**
 * Where the picker starts for a time on the table: this match's own booking, the area the form came in with, then (a
 * suggestion or an admin's set time) the first area free for the whole match, so it is plain that a court is being
 * booked. Agreeing to the other side's time keeps what they proposed: no court stays no court (nobody is charged for a
 * court they didn't pick).
 */
export function gridDefaultPlace(mode, time, preferredAreaId = null, areaOrder = []) {
  const cells = time?.areas ?? [];
  const mine = cells.find((a) => a.status === 'mine');
  if (mine) return {place: 'area', areaId: mine.areaId};
  const preferred = preferredAreaId ? cells.find((a) => a.areaId === preferredAreaId) : null;
  if (preferred && preferred.status === 'free') return {place: 'area', areaId: preferred.areaId};
  if (mode === PICKER_MODES.confirm || mode === PICKER_MODES.accept) return {place: 'later', areaId: null};
  const order = (id) => { const i = areaOrder.indexOf(id); return i < 0 ? 999 : i; };
  const free = cells.filter((a) => a.status === 'free').sort((a, b) => (b.forMatches ? 1 : 0) - (a.forMatches ? 1 : 0) || order(a.areaId) - order(b.areaId));
  return free.length ? {place: 'area', areaId: free[0].areaId} : {place: 'later', areaId: null};
}

/**
 * The summary of the choice: "Thu 30 Oct · 10:00–11:00 · Court 2 · £8.00", always the MATCH's own time (its length,
 * an admin's one-off length included). X214 (Grant 2026-10-28: a 1-hour match on 40-minute slots read "10:00–11:20"):
 * when the court's slots make the booking longer than the match, `booked` says so plainly: "Court 2 booked
 * 10:00–11:20, two 40-minute slots".
 *   {date, time, place, price, booked, line}
 */
export function scheduleSummary(availability, choice, {areaName = null, cell = null, priceMinor = undefined, noun = 'playing area'} = {}) {
  const zone = availability?.timeZone ?? null;
  const date = choice?.date ?? null;
  const startsAt = choice?.startsAt ?? null;
  if (!date || !startsAt) return {date: date ? dateWord(date) : null, time: null, place: null, price: null, booked: null, line: date ? dateWord(date) : ''};
  // F-FIX6 (UX b): an admin's Book anyway past closing books too (to closing): it keeps its Price row and says how far.
  const pastClosing = choice.place === 'area' && !!cell && cell.status === 'closed' && !!cell.pastClosing && !!choice.allowPastClosing;
  const booking = choice.place === 'area' && cell && (cell.status === 'free' || cell.status === 'mine' || pastClosing);
  // F-FIX6 (H1): an americano match's time is its whole session, as booked (bookingMinutesOf).
  const minutes = bookingMinutesOf(availability, choice);
  const matchEnd = new Date(ms(startsAt) + minutes * 60000).toISOString();
  const time = gridRange(startsAt, matchEnd, zone);
  const place = choice.place === 'area' ? (areaName || null)
      : choice.place === 'elsewhere' ? 'Somewhere else'
          : choice.place === 'any' ? `Any free ${noun}` : `No ${noun} booked`;
  const minor = priceMinor !== undefined ? priceMinor : pastClosing ? cell.pastClosingPriceMinor : cell?.priceMinor;
  const price = booking ? (minor > 0 ? formatMinor(minor, availability?.currency || 'gbp') : 'Free') : null;
  let booked = null;
  if (pastClosing) {
    // F-FIX6 (TEST_R99 M-3): the booking holds the court for the whole match; only the time before closing is priced.
    // X240 (TEST_R100 L-2): an americano's rounds back to back are "the session".
    booked = `${areaName || cap(noun)} held for the whole ${bookingNoun(availability)}, past closing at ${cell.closesAtLabel || gridTime(cell.closesAt, zone, {end: true})}`;
  } else if (booking && cell.endsAt && ms(cell.endsAt) > ms(matchEnd)) {
    const area = (availability?.areas ?? []).find((a) => a.id === choice.areaId);
    const slot = Number(area?.slotMinutes) || Number(availability?.slotMinutes) || 60;
    const count = Math.max(1, Math.round((ms(cell.endsAt) - ms(startsAt)) / (slot * 60000)));
    booked = `${areaName || cap(noun)} booked ${gridRange(startsAt, cell.endsAt, zone)}, ${countWord(count)} ${slot}-minute slot${count === 1 ? '' : 's'}`;
  }
  // F-FIX6 (H1): an americano's time is the session; say what it is made of ("3 rounds of 1 hour 30 minutes").
  const rounds = Number(availability?.sessionRounds);
  const session = rounds > 1 ? `The session: ${rounds} rounds of ${lengthWord(Number(choice.durationMinutes) || Number(availability?.matchMinutes))}` : null;
  return {date: dateWord(date), time, place, price, booked, session, line: [dateWord(date), time, place, price].filter(Boolean).join(' · ')};
}

const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const countWord = (n) => COUNT_WORDS[n] ?? String(n);

/** The summary's closing line: the form's note about the time and the line about the court, one space between. */
export function whatHappensLine(note, court) {
  return [note, court].map((x) => String(x ?? '').trim()).filter(Boolean).join(' ');
}

/**
 * The one button's words, saying exactly what happens: "Set this time and book Court 2" · "Confirm this time and book
 * Court 2" · "Propose this time and hold Court 2" · "Propose this time and Court 2" (a suggestion books nothing until
 * it is agreed, BE1) · "Propose this time" (no court).
 */
export function schedulePrimaryLabel({mode, place, areaName = null, hold = false, idle = false, noun = 'playing area', short = false} = {}) {
  const verb = mode === PICKER_MODES.set ? 'Set' : (mode === PICKER_MODES.confirm || mode === PICKER_MODES.accept) ? 'Confirm' : 'Propose';
  const base = `${verb} this time`;
  if (idle) return base;
  const what = place === 'area' ? areaName : place === 'any' ? `any free ${noun}` : null;
  if (!what) return base;
  // X219 (R89 L-1): a phone's full-width button says it in fewer words ("Propose and hold Court 1").
  const lead = short ? verb : base;
  if (verb === 'Propose') return hold && place === 'area' ? `${lead} and hold ${what}` : short ? `${lead} with ${what}` : `${base} and ${what}`;
  return `${lead} and book ${what}`;
}

/**
 * The one sentence under the summary about the court (the time's own note, "Alex will need to confirm this time", sits
 * above it). Never a wait or a duration for a payment.
 */
export function courtLine({mode, place, areaName = null, hold = false, otherName = null, idle = null, noun = 'playing area', alreadyBooked = false} = {}) {
  const other = otherName || 'the other side';
  const named = place === 'area' ? (areaName || `the ${noun}`) : `a free ${noun}`;
  // X243 (TEST_FT-R3 L-B): a member's unchanged time on the court already booked for the match.
  if (alreadyBooked && place === 'area' && !idle) return `${cap(named)} is already booked for this match.`;
  if (idle) return `${areaName || `The ${noun}`} is ${idle === 'open' ? 'open play then: nothing to book, first come first served' : 'closed then: nothing is booked'}.`;
  if (place === 'elsewhere') return 'Played somewhere else, so nothing is booked here.';
  if (place !== 'area' && place !== 'any') return `No ${noun} is booked. You can book one later.`;
  if (mode === PICKER_MODES.set) return `${cap(named)} is booked for both sides now.`;
  if (mode === PICKER_MODES.propose) {
    if (hold && place === 'area') return `${cap(named)} is held for this match until ${other} replies.`;
    return `Nothing is booked until ${other} agrees. Then ${named} is booked, or another free one if it has gone.`;
  }
  return `Confirming books ${named} for the match. If it has gone, we book another free one.`;
}

const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

/** A clock time that isn't one of the day's starts: what step 2 says (the table only books whole slots). */
export function offSlotLine(hhmm, noun = 'playing area') {
  return `No ${noun} booking starts at ${hhmm ? clock24(parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3, 5), 10)) : 'that time'}. Pick a start in the table, or keep the time with no ${noun}.`;
}

/** The instant of a club-zone day and "HH:mm" (the date picker's choice, read on the club's clock). */
export function startsAtFor(date, hhmm, zone) {
  if (!date || !hhmm) return null;
  const at = instantInZone(date, hhmm, zone);
  return at ? new Date(at).toISOString() : null;
}

/**
 * The button's words straight from the picker's choice (the dialogs that hold the picker): the chosen area's name,
 * and nothing to book when the area is open play or closed then, or the day has no booking hours.
 */
export function pickerPrimaryLabel(availability, choice, mode, {short = false} = {}) {
  const noun = areaNounOf(availability);
  const {time} = findTime(availability, choice?.startsAt);
  const cell = chosenCell(time, choice);
  const areaName = choice?.place === 'area' ? ((availability?.areas ?? []).find((a) => a.id === choice.areaId)?.name ?? null) : null;
  const idle = choice?.place === 'area' && (!cell || !!cellIdle(cell, choice));
  return schedulePrimaryLabel({mode, place: choice?.place, areaName, hold: !!choice?.hold, idle, noun, short});
}

/**
 * X214 fix (Grant 2026-10-28: rescheduling a match left the table on its skeleton for ever): what step 2 does for the
 * chosen day. 'ready' (in the loaded window), 'gone' (before today), 'load' (ask for a window from that day: the match's
 * own date can sit beyond the first 14 days), 'failed' (asked already and the answer still has no such day: a plain
 * message and Retry, never an endless skeleton), 'loading' (asked, the answer not in yet), 'none' (no day picked).
 */
export function dayLoadState(availability, date, today, {asked = false, loading = false} = {}) {
  if (!date) return 'none';
  if (findDay(availability, date)) return 'ready';
  if (today && date < today) return 'gone';
  if (!asked) return 'load';
  return loading ? 'loading' : 'failed';
}

/** The first load's window: from the match's own day when it is today or later (yyyy-MM-dd), else from today. */
export function firstLoadFrom(date, today) {
  return date && today && date > today ? date : null;
}

// ── X244 (TEST_R102 M-A): a time the table doesn't offer (typed, or one already under way) ──

/** X244: the API's past grace (LadderChallengeScheduling.PastGrace): a form left open a minute or two still counts as now. */
export const PAST_GRACE_MS = 5 * 60000;

/** X244: whether a start has passed, as the API reads it (a time that has passed books nothing). */
export function startHasPassed(startsAt, now = Date.now()) {
  const t = ms(startsAt);
  return !Number.isNaN(t) && t < now - PAST_GRACE_MS;
}

/**
 * X244: the match's own live booking (or its americano session's, `ownBookings` from the API, whole spans) on `areaId`
 * that holds `startsAt`; any one on `areaId` when `startsAt` is left out. Null when none.
 */
export function ownBookingAt(availability, areaId, startsAt = null) {
  const t = ms(startsAt);
  return (availability?.ownBookings ?? []).find((b) => b.areaId === areaId && (startsAt == null || (ms(b.startsAt) <= t && ms(b.endsAt) > t))) ?? null;
}

/**
 * X244 (TEST_R102 M-A): where a time the table doesn't offer keeps the match's court, as the API keeps it:
 *   'unchanged': the match's own time on its own court, which is booked (a session under way today, its start gone from
 *     the table): saving changes nothing, so the court stays ("… is already booked for this match").
 *   'session': an admin moving the whole session (kept.session) to a time that hasn't passed: the API moves every
 *     court's booking there and checks each court as for a picked start (booked, frozen, closed, outside the hours).
 *   null: no court is kept (the old "No court" choice).
 * kept = {areaId, scheduledAt, session}: the match's court and time as they stand, and whether the whole session moves.
 */
export function offTableKeep(availability, kept, startsAt, now = Date.now()) {
  if (!kept?.areaId || !startsAt) return null;
  if (kept.scheduledAt && sameInstantMinute(startsAt, kept.scheduledAt)) return ownBookingAt(availability, kept.areaId, kept.scheduledAt) ? 'unchanged' : null;
  if (kept.session && (availability?.ownBookings ?? []).length && !startHasPassed(startsAt, now)) return 'session';
  // F-FIX7 (Grant 2026-10-30, "Admin can do what they want with a warning"): an admin's single match keeps its court at
  // a time still to come, so the API checks it there; outside the hours its refusal is the Book anyway question.
  if (kept.match && !startHasPassed(startsAt, now)) return 'match';
  return null;
}

/**
 * X247 (TEST_R104 M-1): a typed time that IS a start in the table, where the match's own court (kept.areaId) can't be
 * picked: Too short (it runs past closing, or into a booking or a freeze later) or closed then. The keep-the-court rule
 * (offTableKeep) applies there too, so an admin typing 20:30 at a 21:00 closing gets the same Book anyway question as a
 * tap on that cell or a typed 20:40, never "No court booked" with the booking released. A start whose own slot is taken
 * or frozen, or open play, keeps the table's usual pick.
 */
export function rowKeepsCourt(time, kept) {
  if (!kept?.areaId) return false;
  const c = (time?.areas ?? []).find((a) => a.areaId === kept.areaId);
  if (!c) return false;
  if (c.status === 'closed') return true;
  const slot = c.slotStatus ?? c.status;
  return slot === 'free' && (c.status === 'taken' || c.status === 'frozen');
}

/**
 * X247 (TEST_R104 M-1): what a typed start in the table keeps when the table's own pick finds no court there: 'match' (an
 * admin's single match) or 'session' (an admin's whole session) on the match's court when rowKeepsCourt, as offTableKeep
 * keeps a time off the table; null otherwise (a member, a time that has passed, a round moved on its own).
 */
export function typedRowKeep(availability, kept, time, startsAt, now = Date.now()) {
  if (!rowKeepsCourt(time, kept)) return null;
  const keep = offTableKeep(availability, kept, startsAt, now);
  return keep === 'match' || keep === 'session' ? keep : null;
}

/**
 * X247 (TEST_R104 L-3): step 2's hint and the summary's court for a ladder time that has passed (its save refuses it):
 * nothing about keeping the time with no court.
 */
export const PAST_LADDER_HINT = 'That time has already passed. Pick a time still to come.';
export const PAST_LADDER_PLACE = 'Stays as it is';

const sameInstantMinute = (a, b) => {
  const ta = ms(a);
  const tb = ms(b);
  return !Number.isNaN(ta) && !Number.isNaN(tb) && Math.abs(ta - tb) < 60000;
};

/** X244: what saving a time that has passed does to the match's booking (the API's FixtureService.PastReleased). */
export const PAST_RELEASED = "That time has passed, so nothing is booked: the match's booking is released.";
/** X244: the same for a whole session (FixtureService.PastReleasedSession). */
export const PAST_RELEASED_SESSION = "That time has passed, so nothing is booked: the session's bookings are released.";

/** F-FIX7 (TEST_FT-R5 observation 1): a ladder refuses a time that has passed, so nothing is released. */
export const PAST_REFUSED_LADDER = "A ladder match can't be set to a time that has passed, so its booking stays as it is.";
/** X247 (TEST_R104 L-3): the same for a ladder match with no booking. */
export const PAST_REFUSED_LADDER_UNBOOKED = "A ladder match can't be set to a time that has passed.";

/**
 * X244 (TEST_R102 M-A): the line about the court for a time the table doesn't offer, when the match has a booking (its
 * own or its session's), instead of "No court is booked. You can book one later.":
 *   keep 'unchanged': "Court 1 is already booked for this match."
 *   keep 'session':   "Each court's booking moves with the session to 7:00. If a court is booked or closed then, nothing moves and you're told why."
 *   a time that has passed: PAST_RELEASED (PAST_RELEASED_SESSION for a whole session); PAST_REFUSED_LADDER when
 *     `refusesPast` (a ladder, whose save refuses it)
 *   no court, a round of a session: "No court is booked at 7:00 for this match. The other rounds keep the session's booking."
 *   no court: "No court is booked at 7:00, so the match's booking is released. You can book one later."
 * Null when the match has no booking (the usual courtLine).
 */
export function offTableLine(availability, {keep = null, startsAt = null, areaName = null, noun = 'playing area', session = false, refusesPast = false, zone = null, now = Date.now()} = {}) {
  const named = areaName || `The ${noun}`;
  // F-FIX7: an admin's single match at a time the table doesn't offer keeps its court (with a booking or without).
  if (keep === 'match') return offTableMatchLine(named, startsAt ? gridTime(startsAt, zone) : 'that time');
  const booked = (availability?.ownBookings ?? []).length > 0;
  if (keep === 'unchanged' && booked) return `${named} is already booked for this match.`;
  // F-FIX7: a ladder's save refuses a time that has passed; say so, never that the booking is released. X247 (TEST_R104
  // L-3): with no booking too, never "No court is booked. You can book one later." over a time that can't be set.
  if (refusesPast && startHasPassed(startsAt, now)) return booked ? PAST_REFUSED_LADDER : PAST_REFUSED_LADDER_UNBOOKED;
  if (!booked) return null;
  const at = startsAt ? gridTime(startsAt, zone) : 'that time';
  if (keep === 'session') return `Each ${noun}'s booking moves with the session to ${at}. If a ${noun} is booked or closed then, nothing moves and you're told why.`;
  if (startHasPassed(startsAt, now)) return session ? PAST_RELEASED_SESSION : PAST_RELEASED;
  if (Number(availability?.sessionRounds) > 1) return `No ${noun} is booked at ${at} for this match. The other rounds keep the session's booking.`;
  return `No ${noun} is booked at ${at}, so the match's booking is released. You can book one later.`;
}

/**
 * X244 (TEST_R102 M-A): the API refuses a start that runs outside the hours ("Court 1 closes before the session is
 * over." / "Court 1 opens after the session starts."); for a club admin's whole-session move to a time the table doesn't
 * offer, that refusal is the X237 question. True for those refusals only.
 */
export function outsideHoursRefusal(message) {
  return /\b(closes before the (match|session) is over|opens after the (match|session) starts|is outside its hours for the whole (match|session))\.$/.test(String(message ?? '').trim());
}

/** F-FIX7: the line for an admin's single match kept on its court at a time the table doesn't offer. */
export function offTableMatchLine(areaName, at) {
  return `We'll book ${areaName} for this match at ${at} if it's free then. If that's outside its hours, you're asked first.`;
}

/** X244: the confirm for that refusal, in the X237 confirm's words: "Court 1 opens after the session starts. Book it anyway?" */
export function outsideHoursConfirm(message) {
  return {
    title: 'This runs outside the hours',
    body: `${String(message ?? '').trim()} Book it anyway?`,
    yes: 'Book anyway',
    no: 'Pick another time',
  };
}
